<template>
  <v-expansion-panel>
    <v-expansion-panel-header>
      <div class="d-flex" style="gap: 10px;">
        <v-icon :color="plan.color ? plan.color.hex : null" small>
          fiber_manual_record
        </v-icon>
        {{ `${plan.name} (${getCurrencyCode(plan.currency_id).code})` }}
      </div>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-row>
        <!------------------------------------------>
        <!-- NAME ----------------------------->
        <!------------------------------------------>
        <v-col cols="12" md="6">
          <v-text-field
            :value="plan.name"
            :label="$t('pricing_model.view.plan.name')"
            class="rounded-lg mt-1"
            outlined
            dense
            readonly
          >
          </v-text-field>
        </v-col>
        <!------------------------------------------>
        <!-- FREQUENCY ----------------------------->
        <!------------------------------------------>
        <v-col cols="12" md="3" class="px-4">
          <v-text-field
            :value="frequencyText"
            :label="$t('pricing_model.view.plan.frequency')"
            class="rounded-lg mt-1"
            hide-details
            outlined
            dense
            readonly
          ></v-text-field>
        </v-col>
        <!------------------------------------------>
        <!-- DESCRIPTION ----------------------------->
        <!------------------------------------------>
        <v-col cols="12">
          <v-text-field
            :value="plan.description"
            :label="$t('pricing_model.view.plan.description')"
            class="rounded-lg mt-1"
            outlined
            dense
            readonly
          >
          </v-text-field>
        </v-col>
        <!------------------------------------------>
        <!-- AMOUNT ----------------------------->
        <!------------------------------------------>
        <v-col cols="12" md="6" v-if="!plan.hasCustomAmount">
          <v-text-field
            :value="plan.amount"
            :label="
              `${$t('pricing_model.view.plan.amount')} ${
                getCurrencyCode(plan.currency_id).code
              } (${getCurrencyCode(plan.currency_id).symbol})`
            "
            class="rounded-lg mt-1"
            outlined
            dense
            readonly
          >
          </v-text-field>
        </v-col>
        <!------------------------------------------>
        <!-- FEE_PERCENTAGE ----------------------------->
        <!------------------------------------------>
        <v-col cols="12" md="6" v-if="!plan.hasCustomFee">
          <v-text-field
            :value="plan.fee_percentage"
            :label="`${$t('pricing_model.view.plan.fee_percentage')} %`"
            class="rounded-lg mt-1"
            outlined
            dense
            readonly
          >
          </v-text-field>
        </v-col>
        <!------------------------------------------>
        <!-- LIMITS TABLE ----------------------------->
        <!------------------------------------------>
        <v-col cols="12" v-if="pricingModel.has_limits_table">
          <v-row no-gutters>
            <v-col cols="12">
              <span class="subtitle-2 grey--text text--darken-2">
                Tabla de límites
              </span>
              <!------------------------------------------>
              <!-- LIMITS REGISTERS ----------------------------->
              <!------------------------------------------>
              <v-col cols="12">
                <span
                  class="subtitle-2 grey--text text--darken-2 font-weight-regular"
                >
                  {{
                    $t("pricing_model.view.plan.limits_table.registers.title")
                  }}
                </span>
                <v-row>
                  <v-col cols="12" md="6" class="mt-4 d-flex">
                    <span
                      class="subtitle-2 mr-4 mt-3 grey--text text--darken-2 font-weight-regular"
                    >
                      {{
                        $t(
                          "pricing_model.view.plan.limits_table.registers.from"
                        )
                      }}
                    </span>
                    <v-text-field
                      :value="plan.registers_offset"
                      disabled
                      class="rounded-lg"
                      outlined
                      dense
                    >
                    </v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                    class="mt-4 d-flex"
                    v-if="!plan.hasNoLimits"
                  >
                    <span
                      class="subtitle-2 mr-4 mt-3 grey--text text--darken-2 font-weight-regular"
                    >
                      {{
                        $t("pricing_model.view.plan.limits_table.registers.to")
                      }}
                    </span>
                    <v-text-field
                      :value="plan.registers_limit"
                      disabled
                      class="rounded-lg"
                      outlined
                      dense
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-col>
              <!------------------------------------------>
              <!-- LIMITS INCOMES ----------------------------->
              <!------------------------------------------>
              <v-col cols="12">
                <span
                  class="subtitle-2 grey--text text--darken-2 font-weight-regular"
                >
                  {{ $t("pricing_model.view.plan.limits_table.incomes.title") }}
                </span>
                <v-row>
                  <v-col cols="12" md="6" class="mt-4 d-flex">
                    <span
                      class="subtitle-2 mr-4 mt-3 grey--text text--darken-2 font-weight-regular"
                    >
                      {{
                        $t("pricing_model.view.plan.limits_table.incomes.from")
                      }}
                    </span>
                    <v-text-field
                      :value="plan.incomes_offset"
                      disabled
                      class="rounded-lg"
                      outlined
                      dense
                    >
                    </v-text-field>
                  </v-col>
                  <v-col
                    cols="12"
                    md="6"
                    class="mt-4 d-flex"
                    v-if="!plan.hasNoLimits"
                  >
                    <span
                      class="subtitle-2 mr-4 mt-3 grey--text text--darken-2 font-weight-regular"
                    >
                      {{
                        $t("pricing_model.view.plan.limits_table.incomes.to")
                      }}
                    </span>
                    <v-text-field
                      :value="plan.incomes_limit"
                      disabled
                      class="rounded-lg"
                      outlined
                      dense
                    >
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-col>
            </v-col>
          </v-row>
        </v-col>
        <!------------------------------------------>
        <!-- AUTOMATION ----------------------------->
        <!------------------------------------------>
        <v-col
          cols="12"
          v-for="(service, item) in plan.additional_services"
          :key="item"
        >
          <v-card class="rounded-lg" flat style="border: 1px solid #BCBCBC;">
            <v-card-title
              class="subtitle-1 grey--text text--darken-2 font-weight-regular"
            >
              {{ service.name.charAt(0).toUpperCase() + service.name.slice(1) }}
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="6" v-if="service.hasCustomService">
                  <p
                    class="subtitle-1 grey--text text--darken-2 font-weight-medium"
                  >
                    
                  </p>
                </v-col>
                <v-col cols="6" v-else>
                  <v-text-field
                    :value="service.amount_per_action"
                    :label="
                      `${$t('pricing_model.view.plan.email_cost')} ${
                        getCurrencyCode(plan.currency_id).code
                      } (${getCurrencyCode(plan.currency_id).symbol})`
                    "
                    class="rounded-lg mt-1"
                    outlined
                    dense
                    readonly
                  >
                  </v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
        <!------------------------------------------>
        <!-- ACTIONS ----------------------------->
        <!------------------------------------------>
        <v-col cols="12" class="d-flex justify-end">
          <v-btn v-if="hasLimitsTable(plan)" color="#F04438" @click="remove">
            <v-icon color="white">delete</v-icon>
            <span style="color: white;">
              {{ `${$t("actions.delete")} Plan` }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { mapState } from "vuex";
export default {
  props: {
    plan: { type: Object, required: true, default: () => ({}) },
    savedPlans: { type: Array, required: true, default: () => [] }
  },
  computed: {
    ...mapState({
      currencies: ({ countries: { currencies } }) => currencies,
      pricingModel: ({ pricingModels: { pricingModel } }) => pricingModel
    }),
    frequencyText() {
      return this.plan.frequency === 1
        ? this.$t("pricing_model.view.plan.frequencies.monthly")
        : this.$t("pricing_model.view.plan.frequencies.yearly");
    }
  },
  methods: {
    getCurrencyCode(currency_id) {
      const currency = this.currencies.find(
        currency => currency.id === currency_id
      );
      return currency;
    },
    hasLimitsTable(plan) {
      // Validate if hasLimitsTable to only allow to delete from the last saved plan to prevent breaking the table, else if the pricing model hasn't table of limits the user can delete in any order
      const lastIndex = this.savedPlans.length - 1;

      // Check if the plan is the last one and pricingModel hasLimitsTable is true, or pricingModel has no limits table
      return (
        (this.savedPlans[lastIndex] === plan &&
          this.pricingModel.has_limits_table) ||
        !this.pricingModel.has_limits_table
      );
    },
    remove() {
      this.$emit("delete-plan");
    }
  }
};
</script>

<style lang="scss" scoped></style>
